import { ChevronsUpDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { SortMode, Subcategory } from "./scoring-category-types";

interface DriverToolbarProps {
  subcategories: Subcategory[];
  activeSubcategoryIdx: number;
  onFilterBySubcategory: (idx: number) => void;
  sortMode: SortMode;
  onSortChange: (mode: SortMode) => void;
  allExpanded: boolean;
  onToggleExpand: () => void;
}

export function DriverToolbar({
  subcategories,
  activeSubcategoryIdx,
  onFilterBySubcategory,
  sortMode,
  onSortChange,
  allExpanded,
  onToggleExpand,
}: DriverToolbarProps) {
  return (
    <div className="flex items-center gap-2 mb-4 flex-wrap">
      {/* Subcategory chips — left */}
      <div className="flex gap-1.5 flex-wrap flex-1 min-w-0">
        {subcategories.map((subcategory, idx) => (
          <button
            key={subcategory.id}
            type="button"
            onClick={() => onFilterBySubcategory(idx)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer",
              idx === activeSubcategoryIdx
                ? "bg-primary/10 border-primary text-primary font-semibold shadow-[0_0_0_1px_color-mix(in_oklch,var(--primary),transparent_70%)]"
                : "bg-card border-border text-muted-foreground hover:border-primary/30 hover:text-foreground",
            )}
          >
            <span className="truncate">{subcategory.label}</span>
            <span
              className={cn(
                "text-[0.625rem] font-semibold px-1.5 rounded-full leading-normal",
                idx === activeSubcategoryIdx
                  ? "bg-primary/20 text-primary"
                  : "bg-muted/70 text-muted-foreground",
              )}
            >
              {subcategory.kpi_drivers.length}
            </span>
          </button>
        ))}
      </div>

      {/* Controls — right */}
      <div className="flex items-center gap-2 shrink-0">
        <Select
          value={sortMode}
          onValueChange={(v) => onSortChange(v as SortMode)}
        >
          <SelectTrigger className="h-7 text-xs w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default</SelectItem>
            <SelectItem value="by-gap">By Gap</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={onToggleExpand}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors cursor-pointer"
        >
          <ChevronsUpDown className="size-3.5" />
          {allExpanded ? "Collapse" : "Expand"}
        </button>
      </div>
    </div>
  );
}
